import {
  LIBRARY_EXPORT_SUBTYPES,
  LIBRARY_EXPORT_TYPES,
  type ComponentExport,
  type LoadedLibraries,
} from '@packages/systems/libraries';

export type LocalDevOverrideMatch = {
  displayName: string;
  realPluginId: string;
  realComponentId: string;
  localPluginId: string;
  localComponentId: string;
};

/**
 * What the Designer knows about the local dev server: whether it answered the
 * last poll (never, while detection is off) and the profile id of the library
 * it is serving.
 */
export type LocalDevServerStatus = {
  isReachable: boolean;
  discoveredProfileId: string | undefined;
};

/** overridden profile id -> pluginId, for every local override's library. */
export function getLocalOverridePluginIdByProfileId(
  libraries: LoadedLibraries
): Map<string, string> {
  const byProfileId = new Map<string, string>();
  for (const [pluginId, library] of Object.entries(libraries)) {
    if (library?.isLocalOverride && library?.overridesProfileId) {
      byProfileId.set(library.overridesProfileId, pluginId);
    }
  }
  return byProfileId;
}

/**
 * profile id -> pluginId, for every installed real (non-local-dev) library. A
 * local-dev library is represented by the real library it overrides.
 */
export function getInstalledRealPluginIdByProfileId(
  libraries: LoadedLibraries
): Map<string, string> {
  const byProfileId = new Map<string, string>();
  for (const [pluginId, library] of Object.entries(libraries)) {
    if (!library?.isLocalOverride && library?.profileId) {
      byProfileId.set(library.profileId, pluginId);
    }
  }
  return byProfileId;
}

/**
 * Pairs a local-dev library with the real installed library it overrides, by
 * the real library's profile id, then pairs their code components by display
 * name. A local library that overrides no profile never matches. This is the
 * structural match only; ask `getLiveLocalDevOverrideMatches` whether an
 * override applies right now.
 */
export function computeLocalDevOverrideMatches(
  libraries: LoadedLibraries
): LocalDevOverrideMatch[] {
  const localByProfileId = new Map<
    string,
    {pluginId: string; componentIdByName: Map<string, string>}
  >();

  for (const [pluginId, library] of Object.entries(libraries)) {
    if (!library.isLocalOverride || !library.overridesProfileId) continue;
    const componentIdByName = new Map<string, string>();
    for (const libraryExport of library.exports) {
      if (
        libraryExport.type === LIBRARY_EXPORT_TYPES.COMPONENT &&
        libraryExport.subtype === LIBRARY_EXPORT_SUBTYPES.CODE_COMPONENT
      ) {
        componentIdByName.set(
          libraryExport.metadata.displayName,
          libraryExport.metadata.targetComponentId
        );
      }
    }
    localByProfileId.set(library.overridesProfileId, {
      pluginId,
      componentIdByName,
    });
  }

  const matches: LocalDevOverrideMatch[] = [];
  for (const [realPluginId, library] of Object.entries(libraries)) {
    if (library.isLocalOverride) continue;
    const local = localByProfileId.get(library.profileId);
    if (!local) continue;

    for (const libraryExport of library.exports) {
      if (
        libraryExport.type !== LIBRARY_EXPORT_TYPES.COMPONENT ||
        libraryExport.subtype !== LIBRARY_EXPORT_SUBTYPES.CODE_COMPONENT
      ) {
        continue;
      }
      const displayName = libraryExport.metadata.displayName;
      const localComponentId = local.componentIdByName.get(displayName);
      if (!localComponentId) continue;

      matches.push({
        displayName,
        realPluginId,
        realComponentId: libraryExport.metadata.targetComponentId,
        localPluginId: local.pluginId,
        localComponentId,
      });
    }
  }
  return matches;
}

/**
 * The local override's library the dev server is serving right now.
 * `devlink dev` serves one library at a time, so an override whose profile id
 * no longer matches the live descriptor's is stale. Undefined when the server
 * is unreachable (including detection off), serves nothing, or serves a library
 * that has no local override.
 *
 * The single definition of "live" for registration, the canvas chips, the
 * Components panel and the extra props section. A local override that is
 * not live does nothing: its components only render while the server is up.
 */
export function getLiveLocalDevPluginId(
  libraries: LoadedLibraries,
  {isReachable, discoveredProfileId}: LocalDevServerStatus
): string | undefined {
  if (!isReachable || !discoveredProfileId) return undefined;
  return getLocalOverridePluginIdByProfileId(libraries).get(
    discoveredProfileId
  );
}

/** What a real component is looked up by in `getOverriddenComponentKeys`. */
export const localDevOverrideComponentKey = (
  pluginId: string,
  componentId: string
): string => `${pluginId}::${componentId}`;

/** The keys of the real components the given matches override. */
export function getOverriddenComponentKeys(
  matches: LocalDevOverrideMatch[]
): Set<string> {
  return new Set(
    matches.map((match) =>
      localDevOverrideComponentKey(match.realPluginId, match.realComponentId)
    )
  );
}

/** The real/local component pairs whose override is live right now. */
export function getLiveLocalDevOverrideMatches(
  libraries: LoadedLibraries,
  status: LocalDevServerStatus
): LocalDevOverrideMatch[] {
  const livePluginId = getLiveLocalDevPluginId(libraries, status);
  return livePluginId
    ? computeLocalDevOverrideMatches(libraries).filter(
        (match) => match.localPluginId === livePluginId
      )
    : [];
}

/**
 * The local-dev library's components its real library lacks, by display name.
 * The Components panel lists them in the real library's section, and the
 * Libraries row counts them. A local component with the id of a real one is
 * left out: both would render as one row.
 */
export function getLocalOnlyComponentExports(
  realLibrary: LoadedLibraries[string],
  localLibrary: LoadedLibraries[string]
): ComponentExport[] {
  const realDisplayNames = new Set<string>();
  const realComponentIds = new Set<string>();
  for (const libraryExport of realLibrary.exports) {
    if (libraryExport.type === LIBRARY_EXPORT_TYPES.COMPONENT) {
      realDisplayNames.add(libraryExport.metadata.displayName);
      realComponentIds.add(libraryExport.metadata.targetComponentId);
    }
  }

  const localOnly: ComponentExport[] = [];
  for (const libraryExport of localLibrary.exports) {
    if (
      libraryExport.type === LIBRARY_EXPORT_TYPES.COMPONENT &&
      !realDisplayNames.has(libraryExport.metadata.displayName) &&
      !realComponentIds.has(libraryExport.metadata.targetComponentId)
    ) {
      localOnly.push(libraryExport);
    }
  }
  return localOnly;
}
